import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'

import { useLoadTemplate } from './useLoadTemplate'
import { CreateAgreementFormModel, TemplateError } from '../../typings'
import {
  ITemplate,
  selectAllAgreementTemplates,
  selectSelectedTemplate,
} from '@user/store/agreements'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'
import { ENTITY_TYPES } from '@sales-center/common'
import { useData } from '../../../../../features/EntityDetails/hooks'

export function useFormFieldActions(
  entityId: number,
  entityType: ENTITY_TYPES,
) {
  const {
    dirty,
    isValid,
    setValues,
    submitForm,
    isValidating,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    values: { templateId },
    errors,
    setFieldError,
  } = useFormikContext<CreateAgreementFormModel>()

  const [showPreview, setShowPreview] = useState(false)

  useLoadTemplate(templateId, entityId, entityType, undefined)

  const services = useSelector(selectAllLeadOppProducts)

  const templates = useSelector(selectAllAgreementTemplates)

  const { template, isLoading } = useSelector(selectSelectedTemplate)

  const { details } = useData(entityType)

  const showPestPacLocationError = useMemo(
    () =>
      template &&
      template?.isCardCaptureEnabled &&
      details &&
      details?.externalLocationCode == null,
    [template, details],
  )

  const handleTemplateChange = useCallback(
    (value: ITemplate) => {
      const templateId = value?.templateId

      const templateFormName = value?.templateFormName ?? ''

      setValues((v) => ({
        ...v,
        templateId,
        templateFormName,
      }))

      if (template) {
        const { dynamicServiceBlock, numOfServices } = template

        if (!dynamicServiceBlock && services.length > numOfServices) {
          setFieldError('templateId', 'required')
        }
      }
    },
    [setValues, template, services, setFieldError],
  )

  const handleTemplateClear = useCallback(() => {
    setFieldValue('templateId', '')
  }, [setFieldValue])

  const handleSelectOpen = useCallback(() => {
    setTimeout(() => {
      setShowPreview(true)
    }, 5)
  }, [setShowPreview])

  const handleSelectClose = useCallback(() => {
    setShowPreview(false)
  }, [setShowPreview])

  useEffect(() => {
    if (template) {
      const { dynamicServiceBlock, willDynamicServiceBlockFit } = template

      // include detail by default if dsb will not fit
      const includeDetail = dynamicServiceBlock && !willDynamicServiceBlockFit

      setFieldValue('includeDetail', includeDetail)
      setFieldTouched('templateId', true)
    }
  }, [setFieldValue, template, setFieldTouched])

  const templateError = useMemo<TemplateError>(() => {
    if (!!template && !isLoading) {
      const { dynamicServiceBlock, numOfServices } = template

      const exceedsServiceSlots =
        !dynamicServiceBlock && services.length > numOfServices

      const distinctSvcLocs = new Set(services.map((s) => s.serviceLocationId))

      const exceedsServiceLocations =
        !dynamicServiceBlock && distinctSvcLocs.size > 1

      return {
        exceedsServiceSlots,
        exceedsServiceLocations,
      }
    }

    return {
      exceedsServiceSlots: false,
      exceedsServiceLocations: false,
    }
  }, [isLoading, services, template])

  const disableSave = useMemo(() => {
    return (
      templateError.exceedsServiceSlots ||
      templateError.exceedsServiceLocations ||
      showPestPacLocationError ||
      !dirty ||
      !isValid ||
      isValidating ||
      isSubmitting ||
      isLoading
    )
  }, [
    templateError,
    showPestPacLocationError,
    dirty,
    isValid,
    isValidating,
    isSubmitting,
    isLoading,
  ])

  const value = useMemo(
    () => templates?.find((row) => row?.templateId == templateId),
    [templates, templateId],
  )

  return {
    template,
    services,
    isLoading,
    templates,
    templateId,
    submitForm,
    disableSave,
    templateError,
    handleTemplateChange,
    isValidating,
    showPreview,
    setShowPreview,
    handleSelectOpen,
    handleSelectClose,
    handleTemplateClear,
    showPestPacLocationError,
    value,
    errors,
  }
}
